import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import BookingForm from "@/components/BookingForm";
import { ArrowLeft, Check } from "lucide-react";
import Seo from "@/components/Seo";

export default function SpaceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (slug === "single-desk") {
      navigate("/book-desk", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/spaces/${slug}`);
        setSpace(res.data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, navigate]);

  if (loading) {
    return (
      <main className="max-w-[1440px] mx-auto px-6 md:px-12 py-24" data-testid="space-detail-loading">
        <p className="overline" style={{ color: "var(--text-secondary)" }}>Laden…</p>
      </main>
    );
  }

  if (notFound || !space) {
    return (
      <main className="max-w-[1440px] mx-auto px-6 md:px-12 py-24" data-testid="space-detail-notfound">
        <p className="overline" style={{ color: "var(--accent)" }}>Niet gevonden</p>
        <h1 className="font-serif text-4xl mt-4 font-light">Die ruimte bestaat niet.</h1>
        <Link to="/" className="btn-ghost mt-8 inline-block">Terug naar het pand</Link>
      </main>
    );
  }

  return (
    <main data-testid={`space-detail-${slug}`}>
      <Seo
        title={`${space.name} huren in Roeselare — Noordstraat 140`}
        description={`${space.name} in Roeselare: ${space.tagline} ${space.summary?.slice(0, 140)}`}
        canonical={`https://www.noordstraat140.com/spaces/${slug}`}
      />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-10 pb-6">
        <Link to="/#spaces" className="overline inline-flex items-center gap-2 hover:text-[var(--accent)]"
              data-testid="back-to-spaces">
          <ArrowLeft size={14} /> Alle ruimtes
        </Link>
      </div>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-12 items-end">
          <div className="col-span-12 md:col-span-7">
            <p className="overline" style={{ color: "var(--accent)" }}>{space.name}</p>
            <h1
              className="font-serif font-light tracking-tighter leading-[0.95] mt-5"
              style={{ fontSize: "clamp(40px, 6.4vw, 96px)" }}
            >
              {space.tagline}
            </h1>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              {space.summary}
            </p>
            <div className="grid grid-cols-2 gap-6 mt-8 pt-8 border-t" style={{ borderColor: "var(--border-color)" }}>
              <div>
                <p className="overline" style={{ color: "var(--text-secondary)" }}>Vanaf</p>
                <p className="font-serif text-2xl mt-1">{space.price_from}</p>
              </div>
              <div>
                <p className="overline" style={{ color: "var(--text-secondary)" }}>Capaciteit</p>
                <p className="font-serif text-2xl mt-1">{space.capacity}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 md:mt-16 aspect-[16/9] overflow-hidden">
          <img src={space.image} alt={space.name} className="w-full h-full object-cover" data-testid="space-hero-image" />
        </div>
      </section>

      {space.images && space.images.length > 1 && (
        <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-16 md:pt-20" data-testid="space-gallery">
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            {space.images.slice(1).map((src, i) => {
              const caption = space.image_captions && space.image_captions[src];
              return (
                <div
                  key={src}
                  className={
                    i % 3 === 0
                      ? "col-span-12 md:col-span-7 aspect-[5/4] overflow-hidden relative"
                      : i % 3 === 1
                      ? "col-span-12 md:col-span-5 aspect-[5/4] overflow-hidden relative"
                      : "col-span-12 aspect-[16/8] overflow-hidden relative"
                  }
                >
                  <img
                    src={src}
                    alt={`${space.name} — beeld ${i + 2}`}
                    className="w-full h-full object-cover"
                    data-testid={`space-gallery-img-${i}`}
                  />
                  {caption && (
                    <span
                      className="absolute bottom-3 left-3 px-2 py-1 text-[10px] tracking-[0.2em] uppercase"
                      style={{
                        backgroundColor: "rgba(46,38,32,0.7)",
                        color: "var(--footer-text)",
                        backdropFilter: "blur(4px)",
                      }}
                      data-testid={`space-gallery-caption-${i}`}
                    >
                      {caption}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <p className="overline" style={{ color: "var(--accent)" }}>Voorzieningen</p>
            <div className="divider mt-5" />
          </div>
          <div className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {space.amenities.map((a) => (
              <div key={a} className="flex items-start gap-3 py-2">
                <Check size={16} style={{ color: "var(--accent)" }} className="mt-1 shrink-0" />
                <span className="text-base">{a}</span>
              </div>
            ))}
            {space.note && (
              <p
                className="col-span-1 sm:col-span-2 text-sm italic mt-4 pt-4 border-t"
                style={{ color: "var(--text-secondary)", borderColor: "var(--border-color)" }}
                data-testid="space-note"
              >
                {space.note}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="relative" style={{ backgroundColor: "var(--bg-surface)" }}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <p className="overline" style={{ color: "var(--accent)" }}>Reserveren</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight mt-5 tracking-tight">
              Vertel ons wat je in gedachten hebt.
            </h2>
            <p className="mt-6 text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Stuur een korte aanvraag en we komen snel terug met
              beschikbare momenten, een aanbod op maat en eventuele
              extra's.
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <BookingForm
              spaceSlug={space.slug}
              spaceName={space.name}
              hideSchedule={space.slug === "kantoorunits"}
              meetingMode={space.slug === "grote-vergaderzaal" || space.slug === "kleine-vergaderruimte"}
            />
          </div>
        </div>
      </section>
    </main>
  );
}
